import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabaseClient'
import SectionNavPills from '../components/SectionNavPills'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const Dashboard = () => {
  const navigate = useNavigate()
  const { user, signOut, authError, forceLogout, hasPaidAccess } = useAuth() // Use canonical access
  const [tests, setTests] = useState([])
  const [lockedTests, setLockedTests] = useState([])
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState(null)
  const [testAttempts, setTestAttempts] = useState({}) // Map of test_id -> attempt data

  // Handle auth errors - redirect to login
  useEffect(() => {
    if (authError) {
      // Force logout and redirect
      forceLogout().then(() => {
        navigate('/login', { replace: true })
      })
    }
  }, [authError, forceLogout, navigate])

  useEffect(() => {
    if (user) {
      // Check if coming from successful payment
      const paymentTrigger = localStorage.getItem('payment_success_trigger')
      if (paymentTrigger) {
        // Clear the flag immediately
        localStorage.removeItem('payment_success_trigger')
      }
      
      loadDashboardData()
    } else if (!user && !authError) {
      // No user and no error - just not logged in
      setLoading(false)
    }
  }, [user, authError])

  const loadDashboardData = async () => {
    try {
      setLoading(true)

      // Get user profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single()
      
      if (profileError) {
        console.error('[Dashboard] Profile error:', profileError.message)
      } else {
        setProfile(profileData)
      }

      // Get session token
      let accessToken = null
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
        if (sessionError) {
          console.error('[Dashboard] Session error:', sessionError.message)
        } else if (sessionData?.session?.access_token) {
          accessToken = sessionData.session.access_token
        }
      } catch (sessionErr) {
        console.error('[Dashboard] Session fetch error:', sessionErr.message || String(sessionErr))
      }

      // Fetch tests from backend
      const headers = {
        'Content-Type': 'application/json',
      }
      
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`
      }

      const response = await fetch(`${BACKEND_URL}/api/my-tests`, { headers })

      if (response.ok) {
        const data = await response.json()
        
        // Only set tests - hasPaidAccess comes from AuthContext
        setTests(data.tests || [])
        setLockedTests(data.locked_tests || [])
        
        // Fetch user's test attempts
        if (accessToken && data.tests?.length > 0) {
          await fetchTestAttempts(accessToken, data.tests)
        }
      } else {
        const errorText = await response.text()
        console.error('[Dashboard] Failed to fetch tests:', response.status, errorText)
      }
    } catch (error) {
      console.error('[Dashboard] Error loading dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch test attempts for each test
  const fetchTestAttempts = async (accessToken, tests) => {
    try {
      const attemptsMap = {}
      
      for (const test of tests) {
        const response = await fetch(
          `${BACKEND_URL}/api/tests/${test.id}/my-attempts`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          }
        )
        
        if (response.ok) {
          const attempts = await response.json()
          if (attempts && attempts.length > 0) {
            // Get the latest completed attempt
            const latestAttempt = attempts
              .filter(a => a.completed)
              .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))[0]
            
            if (latestAttempt) {
              attemptsMap[test.id] = {
                completed: true,
                percentage: latestAttempt.percentage,
                completedAt: latestAttempt.submitted_at || latestAttempt.completed_at || latestAttempt.evaluation_completed_at
              }
            }
          }
        }
      }
      
      setTestAttempts(attemptsMap)
    } catch (error) {
      console.error('[Dashboard] Failed to fetch attempts:', error)
    }
  }


  const handleSignOut = async () => {
    try {
      // signOut is now guaranteed to work (clears local state first)
      await signOut()
      navigate('/')
    } catch (err) {
      // Even if error, signOut cleared local state
      // Force navigate anyway
      console.warn('[Dashboard] Logout error (navigating anyway):', err)
      navigate('/')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Načítavam...</p>
        </div>
      </div>
    )
  }

  // --- Phase 2: partition + group + sort (presentation only) ---
  const isPracticeTest = (t) => t.is_free === true || t.access_reason === 'free'
  const practiceTests = tests.filter(isPracticeTest)
  const regularTests = tests.filter((t) => !isPracticeTest(t))

  // Group regular tests by school_year (desc), sort inside each group deterministically
  const sortWithinGroup = (arr) =>
    [...arr].sort((a, b) => {
      const da = a.release_date || ''
      const db = b.release_date || ''
      if (da !== db) return da < db ? -1 : 1
      const oa = a.release_order ?? 0
      const ob = b.release_order ?? 0
      if (oa !== ob) return oa - ob
      const ta = a.title || ''
      const tb = b.title || ''
      if (ta !== tb) return ta.localeCompare(tb, 'sk')
      return (a.id || '').localeCompare(b.id || '')
    })

  const groupedRegular = Object.entries(
    regularTests.reduce((acc, t) => {
      const sy = t.school_year || 'Bez školského roka'
      if (!acc[sy]) acc[sy] = []
      acc[sy].push(t)
      return acc
    }, {})
  )
    .sort(([a], [b]) => {
      // Newest school year first. Compare the first year number of "YYYY/YYYY".
      const ay = parseInt((a.split('/')[0] || '0'), 10) || 0
      const by = parseInt((b.split('/')[0] || '0'), 10) || 0
      if (ay !== by) return by - ay
      return a.localeCompare(b)
    })
    .map(([schoolYear, list]) => [schoolYear, sortWithinGroup(list)])

  const practiceTestsSorted = sortWithinGroup(practiceTests)

  const formatReleaseDate = (iso) => {
    if (!iso || typeof iso !== 'string') return null
    const [y, m, d] = iso.split('-')
    if (!y || !m || !d) return null
    return `${d}.${m}.${y}`
  }

  const renderTestCard = (test) => (
    <div
      key={test.id}
      className={`group bg-white rounded-2xl border border-slate-100 ring-1 ring-slate-50 shadow-sm overflow-hidden transition-all duration-200 ${
        test.is_unlocked
          ? 'hover:shadow-lg hover:-translate-y-0.5 hover:ring-blue-100 cursor-pointer'
          : 'opacity-75'
      }`}
      onClick={() => test.is_unlocked && navigate(`/test/${test.id}`)}
      data-testid={`test-card-${test.id}`}
    >
      <div className="p-6">
        <div className="flex justify-between items-start gap-3 mb-4">
          <div className="min-w-0">
            <h3 className="font-bold text-lg sm:text-xl text-gray-900 mb-1 leading-snug">
              {test.title}
            </h3>
            <p className="text-sm text-gray-500 capitalize">
              {test.subject}
            </p>
            {formatReleaseDate(test.release_date) && (
              <p
                className="text-xs text-gray-500 mt-1"
                data-testid={`test-card-release-date-${test.id}`}
              >
                Dostupné od: {formatReleaseDate(test.release_date)}
              </p>
            )}
          </div>
          {test.is_unlocked ? (
            test.admin_only ? (
              <span
                className="flex-shrink-0 px-3 py-1 bg-purple-50 text-purple-700 ring-1 ring-purple-200 text-xs font-semibold rounded-full inline-flex items-center gap-1"
                data-testid={`test-card-admin-only-badge-${test.id}`}
                title="Tento test je viditeľný len pre adminov. Bežní používatelia ho neuvidia."
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M10 2a4 4 0 00-4 4v3H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-1V6a4 4 0 00-4-4zm-2 7V6a2 2 0 114 0v3H8z" />
                </svg>
                ADMIN náhľad
              </span>
            ) : (
              <span className="flex-shrink-0 px-3 py-1 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-xs font-semibold rounded-full inline-flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {hasPaidAccess ? 'Dostupný' : 'Bezplatný'}
              </span>
            )
          ) : (
            <span className="flex-shrink-0 px-3 py-1 bg-rose-50 text-rose-700 ring-1 ring-rose-200 text-xs font-semibold rounded-full inline-flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Zamknutý
            </span>
          )}
        </div>

        <p className="text-gray-600 text-sm mb-4 leading-snug">
          {test.description || 'Pripravte sa na Testovanie 9'}
        </p>

        <div className="flex justify-between items-center text-sm text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            {test.total_questions || 15} otázok
          </span>
          <span className="inline-flex items-center gap-1.5">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {test.duration_minutes || 45} min
          </span>
        </div>

        {/* Test Status - Show completion info */}
        {test.is_unlocked && testAttempts[test.id] && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  Dokončené
                </span>
                {testAttempts[test.id].completedAt && !isNaN(new Date(testAttempts[test.id].completedAt).getTime()) && (
                  <span className="text-xs text-gray-500">
                    {new Date(testAttempts[test.id].completedAt).toLocaleDateString('sk-SK')}
                  </span>
                )}
              </div>
              <div className="px-2.5 py-1 bg-blue-50 ring-1 ring-blue-100 rounded-full text-xs font-bold text-blue-700">
                {Math.round(testAttempts[test.id].percentage)}%
              </div>
            </div>
          </div>
        )}

        {test.is_unlocked && !testAttempts[test.id] && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
              <span className="text-xs text-gray-500">
                Ešte nedokončené
              </span>
            </div>
          </div>
        )}

        {/* Only show purchase message for logged-in unpaid users on locked tests */}
        {!test.is_unlocked && user && !hasPaidAccess && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs text-gray-500 text-center">
              Zakúpte balíček EduMisia pre úplný prístup
            </p>
          </div>
        )}
      </div>

      {test.is_unlocked && (
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border-t border-slate-100 px-6 py-3.5">
          <button className="text-blue-700 font-semibold text-sm hover:text-blue-800 inline-flex items-center justify-center w-full gap-2">
            Začať test
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  )


  return (
    <div className="min-h-screen bg-transparent">
      <SectionNavPills />
      <div className="container mx-auto px-4 py-8 md:py-10">

        {/* "Moje testy" intro card — calm EduMisia style */}
        <section
          className="bg-gradient-to-br from-white via-blue-50/30 to-emerald-50/20 border border-slate-200 ring-1 ring-slate-100 rounded-2xl shadow-md shadow-slate-200/40 p-6 sm:p-8 mb-6 md:mb-8"
          data-testid="my-tests-intro"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Moje testy
              </h1>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl">
                Tu vidíte dostupné testy, svoj progres a môžete pokračovať v príprave na Testovanie 9.
              </p>
              <p className="text-sm text-gray-500 mt-2 max-w-2xl">
                Pokračujte tam, kde ste skončili, alebo si pozrite dokončené testy a výsledky.
              </p>
            </div>

            {!loading && user && (
              <div className="flex-shrink-0">
                {hasPaidAccess ? (
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 rounded-full font-medium text-sm" data-testid="access-active-badge">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Aktívny prístup
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate('/pricing')}
                    aria-label="Zobraziť cenník a získať plný prístup"
                    title="Zobraziť cenník"
                    className="group inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-blue-50 hover:text-blue-700 hover:ring-blue-200 active:bg-blue-100 rounded-full font-medium text-sm transition cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1"
                    data-testid="free-access-badge"
                  >
                    Bezplatný prístup
                    <svg
                      className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Quick Stats for Paid Users */}
          {hasPaidAccess && tests.length > 0 && (() => {
            // Step 16b: derive overview stats from `testAttempts` (the same source
            // the test cards use), not from `t.attempts` (which is not part of
            // the /api/my-tests payload). This keeps the overview consistent
            // with what each card actually shows.
            const completedIds = tests.filter((t) => testAttempts[t.id]).map((t) => t.id)
            const completedCount = completedIds.length
            const availableCount = tests.filter(
              (t) => (t.is_unlocked || t.is_published) && !testAttempts[t.id]
            ).length
            const totalCount = tests.length

            const validPercentages = completedIds
              .map((id) => testAttempts[id]?.percentage)
              .filter((p) => typeof p === 'number' && !Number.isNaN(p))

            const averagePct =
              validPercentages.length > 0
                ? Math.round(
                    validPercentages.reduce((s, p) => s + p, 0) /
                      validPercentages.length
                  )
                : null

            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="my-tests-stats">
                <div className="rounded-xl bg-emerald-50/40 border border-emerald-100 p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-700 mb-1" data-testid="stat-completed">
                    {completedCount}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Dokončené</div>
                </div>
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 text-center">
                  <div className="text-2xl font-bold text-gray-900 mb-1" data-testid="stat-available">
                    {availableCount}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Dostupné</div>
                </div>
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 text-center">
                  <div className="text-2xl font-bold text-gray-900 mb-1" data-testid="stat-total">
                    {totalCount}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Celkom</div>
                </div>
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700 mb-1" data-testid="stat-average">
                    {averagePct === null ? '–' : `${averagePct}%`}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Priemer</div>
                </div>
              </div>
            )
          })()}
        </section>

        {/* Show tests for both free and paid users, but with different UI */}
        <div>
          {/* Calm upgrade card for logged-in unpaid users (replaces the old image-banner with baked-in price text). */}
          {user && !hasPaidAccess && (
            <div
              className="group max-w-3xl mx-auto mb-8 rounded-2xl bg-gradient-to-br from-blue-50 via-white to-indigo-50 border border-blue-200 ring-1 ring-blue-100 shadow-sm hover:shadow-md p-5 sm:p-6 cursor-pointer transition-all duration-200"
              onClick={() => navigate('/pricing')}
              data-testid="upgrade-banner"
            >
              <div className="flex items-start gap-4">
                <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white inline-flex items-center justify-center shadow-sm shadow-blue-500/30 ring-4 ring-blue-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg mb-1">
                    Odomknite všetky testy
                  </h3>
                  <p className="text-sm text-gray-700 leading-snug mb-3">
                    Zakúpte balíček EduMisia za <span className="font-semibold text-blue-700">14,99 € / školský rok</span> a získajte prístup ku všetkým zverejneným testom, výsledkom a vysvetleniam do 31. mája daného školského roka.
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold shadow-sm ring-1 ring-blue-700/20 group-hover:bg-blue-700 transition-colors"
                  >
                    Pozrieť cenník
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 inline-flex items-center gap-3">
              <span aria-hidden="true" className="block w-1.5 h-7 rounded-full bg-gradient-to-b from-blue-600 to-emerald-500"></span>
              Dostupné testy
            </h2>
            <p className="text-sm text-gray-600 ml-[1.125rem]">
              {hasPaidAccess
                ? 'Vyberte si test, ktorý chcete absolvovať.'
                : 'Cvičný test je dostupný bezplatne pre prihlásených používateľov.'}
            </p>
          </div>

          {tests.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 ring-1 ring-slate-50 shadow-sm p-8 text-center">
              <p className="text-gray-600">Zatiaľ nie sú dostupné žiadne testy</p>
            </div>
          ) : (
            <div className="space-y-10">
              {/* Cvičné testy (practice / sample, is_free=true) */}
              {practiceTestsSorted.length > 0 && (
                <section data-testid="practice-tests-section">
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-900 inline-flex items-center gap-2.5">
                      <span aria-hidden="true" className="block w-1 h-5 rounded-full bg-gradient-to-b from-emerald-500 to-blue-500"></span>
                      Cvičné testy
                    </h3>
                    <p className="text-sm text-gray-600 ml-[0.875rem]">
                      Vyskúšaj si, ako funguje testovanie.
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {practiceTestsSorted.map(renderTestCard)}
                  </div>
                </section>
              )}

              {/* Regular tests grouped by school_year */}
              {groupedRegular.map(([schoolYear, list]) => (
                <section
                  key={schoolYear}
                  data-testid={`school-year-group-${schoolYear.replace('/', '-')}`}
                >
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-900 inline-flex items-center gap-2.5">
                      <span aria-hidden="true" className="block w-1 h-5 rounded-full bg-gradient-to-b from-blue-600 to-indigo-500"></span>
                      Školský rok {schoolYear}
                    </h3>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {list.map(renderTestCard)}
                  </div>
                </section>
              ))}

              {/* "Pokračuj v príprave" — value-discovery section for logged-in
                  users without a Testovanie 9 package. Shows safe title/topic
                  metadata for paid tests they cannot currently open. Backend
                  authorization on the actual test content remains fully
                  enforced (check_test_access). */}
              {user && !hasPaidAccess && lockedTests.length > 0 && (
                <section data-testid="locked-tests-section" aria-labelledby="locked-tests-heading">
                  <div className="mb-4">
                    <h3 id="locked-tests-heading" className="text-xl font-bold text-gray-900 inline-flex items-center gap-2.5">
                      <span aria-hidden="true" className="block w-1 h-5 rounded-full bg-gradient-to-b from-amber-500 to-orange-500"></span>
                      Pokračuj v príprave
                    </h3>
                    <p className="text-sm text-gray-700 leading-snug mt-2 max-w-2xl">
                      Jeden test je len začiatok. S balíkom EduMisia môžeš pokračovať v ďalších testoch, zistiť, ktoré témy ti idú a na čom sa ešte oplatí popracovať.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {lockedTests.map((lt) => (
                      <button
                        key={lt.id}
                        type="button"
                        onClick={() => navigate('/pricing')}
                        className="text-left bg-white rounded-2xl border border-slate-200 ring-1 ring-slate-100 hover:ring-blue-200 hover:border-blue-200 hover:shadow-md transition p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        aria-label={`${lt.title} — súčasť balíka. Zobraziť balík.`}
                        data-testid={`locked-test-card-${lt.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-amber-800 bg-amber-50 ring-1 ring-amber-200">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                              <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v3H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-1V6a4 4 0 00-4-4zm-2 7V6a2 2 0 114 0v3H8z" clipRule="evenodd" />
                            </svg>
                            Súčasť balíka
                          </span>
                          {lt.school_year && (
                            <span className="text-[11px] text-gray-500 whitespace-nowrap">{lt.school_year}</span>
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-900 text-sm sm:text-base leading-tight mb-1">
                          {lt.title}
                        </h4>
                        {(lt.topics_text || lt.subject) && (
                          <p className="text-xs text-gray-600 line-clamp-2">
                            {lt.topics_text || lt.subject}
                          </p>
                        )}
                        {(lt.duration_minutes || lt.total_questions) && (
                          <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500">
                            {lt.duration_minutes ? <span>{lt.duration_minutes} min</span> : null}
                            {lt.total_questions ? <span>{lt.total_questions} otázok</span> : null}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="rounded-2xl bg-gradient-to-br from-blue-50 via-white to-indigo-50 border border-blue-200 ring-1 ring-blue-100 p-4 sm:p-5">
                    <p className="text-sm text-gray-700 leading-snug mb-3">
                      <span className="font-semibold text-gray-900">Pre rodiča:</span> získate prehľad o výsledkoch dieťaťa a témach, v ktorých potrebuje viac precvičovania. Dieťa dostane ďalšie testy s výsledkami a vysvetleniami odpovedí.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/pricing')}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm ring-1 ring-blue-700/20 transition"
                      data-testid="locked-tests-view-package-btn"
                    >
                      Pozrieť celý balík
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </button>
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
